/**
 * BOYS LIE — funnel analytics.
 *
 * One dispatch function, one context envelope, and every custom-event listener.
 *
 * Each event fans out to two independent destinations:
 *   - TripleWhale, directly, as a `custom` event via window.TriplePixel.
 *   - The Shopify custom pixel, via Shopify.analytics.publish('boyslie:<name>').
 *     That pixel (pixels/boyslie-events.js) owns all GA4 and Meta mapping;
 *     neither vendor is reachable from this window, so nothing here talks to
 *     gtag or fbq. See
 *     docs/superpowers/specs/2026-09-02-shopify-pixel-ga4-meta-design.md
 *
 * See also
 * docs/superpowers/specs/2026-08-31-triplewhale-funnel-analytics-design.md
 *
 * Property names MUST be snake_case with no spaces: a single property named
 * "URL Visited" broke SQL access to the whole pixel_custom_events_table.
 */
(function () {
  'use strict';

  var KEY_PATTERN = /^[a-z][a-z0-9_]*$/;
  var PIXEL_WAIT_MS = 10000;
  var POLL_MS = 200;
  var SEQ_KEY = 'bl_analytics_seq';

  var PIXEL_EVENT_PREFIX = 'boyslie:';

  var destinations = {
    triplewhale: {
      queue: [],
      timer: null,
      // TriplePixel rejects a custom event with more than 10 properties and
      // then stops executing for the rest of the page, taking native events
      // (add to cart included) down with it. Counts the envelope too.
      maxProps: 10,
      ready: function () {
        return typeof window.TriplePixel === 'function';
      },
      deliver: function (name, props) {
        window.TriplePixel('custom', name, props);
      },
    },
    pixel: {
      queue: [],
      timer: null,
      ready: function () {
        return !!(window.Shopify
          && window.Shopify.analytics
          && typeof window.Shopify.analytics.publish === 'function');
      },
      deliver: function (name, props) {
        window.Shopify.analytics.publish(PIXEL_EVENT_PREFIX + name, props);
      },
    },
  };

  var DEFAULT_DESTINATIONS = ['triplewhale', 'pixel'];

  var startedAt = Date.now();
  var debug = false;

  try {
    debug = new URLSearchParams(window.location.search).get('analytics_debug') === '1';
  } catch (error) {
    debug = false;
  }

  function log() {
    if (!debug) return;
    console.log.apply(console, ['[analytics]'].concat(Array.prototype.slice.call(arguments)));
  }

  function warn() {
    if (!debug) return;
    console.warn.apply(console, ['[analytics]'].concat(Array.prototype.slice.call(arguments)));
  }

  function isValidKey(key) {
    return typeof key === 'string' && KEY_PATTERN.test(key);
  }

  function toCents(value) {
    var number = Number(value);
    return isFinite(number) ? Math.round(number) : 0;
  }

  function sanitizeProps(name, props) {
    var clean = {};

    Object.keys(props || {}).forEach(function (key) {
      var value = props[key];

      if (value === null || value === undefined || value === '') return;

      if (!isValidKey(key)) {
        warn('invalid property name "' + key + '" on "' + name + '" — dropped');
        return;
      }

      if (typeof value === 'object' || typeof value === 'function') {
        warn('non-scalar property "' + key + '" on "' + name + '" — dropped');
        return;
      }

      clean[key] = value;
    });

    return clean;
  }

  function nextSeq() {
    try {
      var next = parseInt(window.sessionStorage.getItem(SEQ_KEY) || '0', 10) + 1;
      window.sessionStorage.setItem(SEQ_KEY, String(next));
      return next;
    } catch (error) {
      return 0;
    }
  }

  function context() {
    return window.__analyticsContext || {};
  }

  function buildEnvelope() {
    var ctx = context();
    var envelope = { path: window.location.pathname, session_seq: nextSeq() };

    if (ctx.page_type) envelope.page_type = ctx.page_type;
    if (ctx.template) envelope.template = ctx.template;

    return envelope;
  }

  function surfaceFor(element) {
    if (element && typeof element.closest === 'function') {
      if (element.closest('quick-buy-popover')) return 'quick_buy_popover';
      if (element.closest('quick-buy-drawer')) return 'quick_buy_drawer';
      if (element.closest('cart-drawer, #shopify-section-mini-cart')) return 'cart_drawer';
      // snippets/cart-reminder.liquid wraps its checkout button (also
      // name="checkout") in `<div class="cartReminder">`, outside both
      // <cart-drawer> and #shopify-section-mini-cart, so without this
      // branch it fell through to the page-type fallback and reported
      // 'other' — one of the three checkout locations the spec names.
      if (element.closest('.cartReminder')) return 'cart_reminder';
    }

    var pageType = context().page_type;
    if (pageType === 'cart') return 'cart_page';
    if (pageType === 'product') return 'pdp';
    return 'other';
  }

  function pixelReady() {
    return destinations.triplewhale.ready();
  }

  // A throwing destination must not take down whatever runs after this call.
  // In the Klaviyo submit handler that is track() -> identifyContact() -> the
  // _learnq identify push; an unguarded throw here silently kills the rest of
  // a live signup handler. warn() (not silence) is the channel for surfacing
  // this in debug mode.
  function send(key, name, props) {
    var maxProps = destinations[key].maxProps;
    var count = Object.keys(props).length;

    if (maxProps && count > maxProps) {
      warn(key + ' allows ' + maxProps + ' properties; "' + name + '" has ' + count + ' — not sent');
      return;
    }

    try {
      destinations[key].deliver(name, props);
      log('sent', key, name, props);
    } catch (error) {
      warn(key + ' threw for "' + name + '"', error);
    }
  }

  function stopPolling(key) {
    var destination = destinations[key];
    if (destination.timer === null) return;
    clearInterval(destination.timer);
    destination.timer = null;
  }

  function flushQueue(key) {
    var destination = destinations[key];
    if (!destination.ready()) return;

    while (destination.queue.length) {
      var queued = destination.queue.shift();
      send(key, queued.name, queued.props);
    }
    stopPolling(key);
  }

  function flushAll() {
    Object.keys(destinations).forEach(flushQueue);
  }

  function startPolling(key) {
    var destination = destinations[key];
    if (destination.timer !== null) return;

    destination.timer = setInterval(function () {
      if (destination.ready()) {
        flushQueue(key);
        return;
      }
      if (Date.now() - startedAt > PIXEL_WAIT_MS) {
        warn(key + ' never loaded — dropping ' + destination.queue.length + ' queued event(s)');
        destination.queue.length = 0;
        stopPolling(key);
      }
    }, POLL_MS);
  }

  function whenPixelReady(callback) {
    if (pixelReady()) {
      callback();
      return;
    }

    var waited = 0;
    var timer = setInterval(function () {
      if (pixelReady()) {
        clearInterval(timer);
        callback();
        return;
      }
      waited += POLL_MS;
      if (waited > PIXEL_WAIT_MS) {
        clearInterval(timer);
        warn('TriplePixel never loaded — identity call dropped');
      }
    }, POLL_MS);
  }

  function identifyContact(email, method) {
    if (!email) return;
    whenPixelReady(function () {
      try {
        window.TriplePixel('Contact', { email: email, method: method });
      } catch (error) {
        warn('TriplePixel threw for the Contact identity call', error);
      }
    });
  }

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
      return;
    }
    callback();
  }

  function track(name, props, options) {
    if (!isValidKey(name)) {
      warn('invalid event name "' + name + '" — dropped');
      return;
    }

    // buildEnvelope() advances session_seq, so it is called ONCE per event,
    // not once per destination: both destinations must record the same
    // ordinal or cross-destination joins break.
    var merged = Object.assign({}, buildEnvelope(), props || {});
    var payload = sanitizeProps(name, merged);
    var keys = (options && options.destinations) || DEFAULT_DESTINATIONS;

    keys.forEach(function (key) {
      if (!destinations[key]) {
        warn('unknown destination "' + key + '" for "' + name + '"');
        return;
      }

      if (destinations[key].ready()) {
        flushQueue(key);
        send(key, name, payload);
        return;
      }

      destinations[key].queue.push({ name: name, props: payload });
      log('queued', key, name, payload);
      startPolling(key);
    });
  }

  function registerLeadEvents() {
    window.addEventListener('klaviyoForms', function (event) {
      var detail = event.detail || {};
      var meta = detail.metaData || {};

      if (detail.type === 'open') {
        track('signup_form_viewed', { form_id: detail.formId, provider: 'klaviyo' });
      }

      if (detail.type === 'submit') {
        track('email_signup_klaviyo', {
          form_id: detail.formId,
          form_source: meta.$source,
          email: meta.$email,
        });

        identifyContact(meta.$email, 'klaviyo');

        // Klaviyo's own property name — the space is intentional and unrelated
        // to the TripleWhale naming rules.
        if (window._learnq) {
          window._learnq.push(['identify', { 'URL Visited': window.location.pathname }]);
        }
      }
    });

    // One signup_form_viewed event for both form vendors, told apart by
    // `provider`, so "all form views" stays a single query and the event
    // keeps its history. Signups stay split by name for the same reason.
    document.addEventListener('alia:popupView', function (event) {
      var detail = event.detail || {};

      track('signup_form_viewed', {
        form_id: detail.popupID,
        campaign_id: detail.campaignID,
        provider: 'alia',
      });
    });

    document.addEventListener('alia:signup', function (event) {
      var email = (event.detail || {}).email;

      track('email_signup_alia', { email: email });
      identifyContact(email, 'alia');
    });

    document.addEventListener('click', function (event) {
      var button = event.target && event.target.closest
        ? event.target.closest('.quantum-lbw-wishlist-btn')
        : null;
      if (!button) return;

      var productId = button.getAttribute('data-quantum-lbw-product-id');

      track('wishlist_added', { product_id: productId, surface: surfaceFor(button) });

      if (window._learnq) {
        window._learnq.push(['track', 'Wish List Add', { id: productId, url: window.location.href }]);
      }
    });

    // Add to cart is already a native TripleWhale event. This listener exists
    // only to keep Klaviyo's Product Added flow trigger working.
    document.addEventListener('variant:added', function (event) {
      var variant = (event.detail || {}).variant;
      if (!variant || !window._learnq) return;

      window._learnq.push(['track', 'Product Added', {
        id: variant.id,
        name: variant.product_title,
        type: variant.product_type,
        url: variant.url,
        productType: variant.product_type,
        imageUrl: variant.image,
        variantSelected: variant.variant_id,
        price: variant.price,
        priceMin: variant.discount_price,
        priceMax: variant.original_price,
        sale: variant.discount_price,
        value: Math.round(variant.price) / 100,
      }]);
    });
  }

  var ATC_DRAWER_WINDOW_MS = 2000;

  var cart = null;
  var lastAddedAt = 0;

  function cartState() {
    var ctx = context();

    if (cart) {
      return {
        cart_value_cents: toCents(cart.total_price),
        cart_item_count: Number(cart.item_count) || 0,
      };
    }

    return {
      cart_value_cents: toCents(ctx.cart_value_cents),
      cart_item_count: Number(ctx.cart_item_count) || 0,
    };
  }

  function cartHasDiscount() {
    if (!cart) return false;
    var applications = cart.cart_level_discount_applications || [];
    return applications.length > 0;
  }

  function shippingEligibleCents(cartObject) {
    if (!cartObject) return 0;

    var items = cartObject.items || [];
    var total = 0;

    items.forEach(function (item) {
      if (item.requires_shipping === false) return;
      total += toCents(item.final_line_price);
    });

    var applications = cartObject.cart_level_discount_applications || [];
    applications.forEach(function (application) {
      total -= toCents(application.total_allocated_amount);
    });

    return total;
  }

  function freeShippingThresholdCents() {
    var bar = document.querySelector('free-shipping-bar[threshold]');
    if (!bar) return null;

    var threshold = toCents(bar.getAttribute('threshold'));
    return threshold > 0 ? threshold : null;
  }

  function variantIdFromHref(href) {
    try {
      var url = new URL(href, window.location.origin);
      var id = parseInt(url.searchParams.get('id'), 10);
      return isFinite(id) ? id : null;
    } catch (error) {
      return null;
    }
  }

  function findCartItem(variantId) {
    if (!cart || !cart.items) return null;

    for (var index = 0; index < cart.items.length; index++) {
      if (Number(cart.items[index].id) === Number(variantId)) return cart.items[index];
    }
    return null;
  }

  function registerCartEvents() {
    // Seed from whatever is readable at registration time — the cart cache
    // is always empty this early (cart:updated hasn't fired yet), so this is
    // the __analyticsContext cart total from analytics-context.liquid. It is
    // an overestimate of the true shipping-eligible amount (it does not
    // subtract non-shipping items or cart-level discounts), which is the
    // safe direction here: it can only suppress a spurious "crossed" latch
    // seed, never cause one to be skipped that genuinely qualifies later.
    // Without this seed, a shopper who loads a page with a cart already over
    // the threshold and then changes anything gets a false "crossed".
    var lastEligibleCents = toCents(context().cart_value_cents);
    var seedThreshold = freeShippingThresholdCents();
    var thresholdCrossed = seedThreshold !== null && lastEligibleCents >= seedThreshold;

    document.addEventListener('cart:updated', function (event) {
      var updated = (event.detail || {}).cart;
      if (!updated) return;

      cart = updated;

      var threshold = freeShippingThresholdCents();
      var eligible = shippingEligibleCents(updated);

      if (threshold === null) {
        // <free-shipping-bar> renders only in the non-empty branch of the
        // mini cart, and that section is swapped in ~600ms AFTER
        // cart:updated fires. A first add from an empty cart can cross the
        // threshold on THIS update, but the bar isn't in the DOM yet to
        // read it. Leave lastEligibleCents untouched (do not advance past
        // this update) so the next cart:updated — whenever it happens to
        // fire — still sees the true before/after transition instead of
        // treating the missing bar as "no threshold configured".
        return;
      }

      if (!thresholdCrossed && lastEligibleCents < threshold && eligible >= threshold) {
        thresholdCrossed = true;
        track('free_shipping_threshold_crossed', {
          threshold_cents: threshold,
          cart_value_cents: toCents(updated.total_price),
        });
      }

      lastEligibleCents = eligible;
    });

    document.addEventListener('discount:result', function (event) {
      var detail = event.detail || {};

      track('discount_attempted', {
        code: detail.code,
        outcome: detail.outcome,
        http_status: detail.http_status,
        surface: surfaceFor(event.target),
      });
    });

    document.addEventListener('variant:added', function () {
      lastAddedAt = Date.now();
    });

    document.addEventListener('openable-element:open', function (event) {
      if (event.target.tagName !== 'CART-DRAWER') return;

      // lastAddedAt starts at the 0 sentinel. Without the `lastAddedAt !== 0`
      // guard, a drawer open with no preceding add-to-cart would compute
      // `Date.now() - 0`, which is < ATC_DRAWER_WINDOW_MS during tests (fake
      // timers start the clock at 0) and can even be true in production in
      // the first two seconds after page load — misreporting a manual open
      // as auto_after_atc. The zero sentinel must never read as "just added".
      var automatic = lastAddedAt !== 0 && Date.now() - lastAddedAt < ATC_DRAWER_WINDOW_MS;
      var props = cartState();

      props.trigger = automatic ? 'auto_after_atc' : 'manual';
      track('cart_drawer_opened', props);
    });

    document.addEventListener('openable-element:close', function (event) {
      if (event.target.tagName !== 'CART-DRAWER') return;

      // Pixel-only: GA4 wants the close for session shape, but the warehouse
      // deliberately excludes close/engagement events on volume grounds.
      track('cart_drawer_closed', cartState(), { destinations: ['pixel'] });
    });

    document.addEventListener('click', function (event) {
      var button = event.target && event.target.closest
        ? event.target.closest('[name="checkout"]')
        : null;
      if (!button) return;

      var props = cartState();

      props.surface = surfaceFor(button);
      props.has_discount = cartHasDiscount();
      track('checkout_clicked', props);
    });

    document.addEventListener('click', function (event) {
      var link = event.target && event.target.closest
        ? event.target.closest('.line-item__remove-button')
        : null;
      if (!link) return;

      var variantId = variantIdFromHref(link.getAttribute('href'));
      if (variantId === null) return;

      var item = findCartItem(variantId);
      var state = cartState();

      track('cart_line_removed', {
        variant_id: variantId,
        product_id: item ? item.product_id : null,
        price_cents: item ? toCents(item.final_line_price) : null,
        cart_value_before_cents: state.cart_value_cents,
        surface: surfaceFor(link),
      });
    });

    // Keyed per <line-item-quantity> element (not a single shared slot): the
    // mini-cart drawer renders one such element per line item, and two
    // quick edits on different lines can interleave their start/end events.
    // A WeakMap keyed on event.target keeps each line's pending state
    // isolated, and entries are dropped automatically when the theme
    // re-renders the cart and discards the old elements.
    var pendingQuantityChanges = new WeakMap();

    document.addEventListener('line-item-quantity:change:start', function (event) {
      var link = event.target.querySelector
        ? event.target.querySelector('.line-item__remove-button')
        : null;
      var variantId = link ? variantIdFromHref(link.getAttribute('href')) : null;
      var item = variantId === null ? null : findCartItem(variantId);

      pendingQuantityChanges.set(event.target, {
        variant_id: variantId,
        previous_quantity: item ? Number(item.quantity) : null,
        surface: surfaceFor(event.target),
      });
    });

    document.addEventListener('line-item-quantity:change:end', function (event) {
      var pending = pendingQuantityChanges.get(event.target);
      pendingQuantityChanges.delete(event.target);

      var detail = event.detail || {};
      var newQuantity = Number(detail.newLineQuantity);

      // Quantity zero is a removal, already tracked on the click.
      if (!pending || !isFinite(newQuantity) || newQuantity === 0) return;

      var direction = null;
      if (pending.previous_quantity !== null) {
        direction = newQuantity > pending.previous_quantity ? 'increase' : 'decrease';
      }

      track('cart_quantity_changed', {
        variant_id: pending.variant_id,
        new_quantity: newQuantity,
        direction: direction,
        surface: pending.surface,
      });
    });
  }

  function optionDelta(variant, previousVariant) {
    var names = context().product_option_names || [];
    var options = (variant && variant.options) || [];
    var previous = (previousVariant && previousVariant.options) || [];

    var position = 0;
    for (var index = 0; index < options.length; index++) {
      if (options[index] !== previous[index]) {
        position = index + 1;
        break;
      }
    }

    // No previous variant, or an identical option set: fall back to the last option,
    // which is Size on this store's products.
    if (position === 0) position = options.length;

    var optionChanged = names[position - 1] || null;

    // option_position is only a fallback for an unresolved option name.
    // Sending both put variant_selected at 11 properties with the envelope,
    // over TripleWhale's limit of 10.
    return {
      option_changed: optionChanged,
      option_position: optionChanged ? null : position || null,
      option_value: options[position - 1] || null,
    };
  }

  function handleFromHref(href) {
    if (!href) return null;

    try {
      var match = new URL(href, window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
      return match ? match[1] : null;
    } catch (error) {
      return null;
    }
  }

  function registerProductEvents() {
    document.addEventListener('variant:change', function (event) {
      var detail = event.detail || {};
      var variant = detail.variant;
      if (!variant) return;

      var delta = optionDelta(variant, detail.previousVariant);
      var surface = surfaceFor(event.target);

      track('variant_selected', {
        product_id: context().product_id,
        variant_id: variant.id,
        option_changed: delta.option_changed,
        option_position: delta.option_position,
        option_value: delta.option_value,
        price_cents: toCents(variant.price),
        surface: surface,
      });

      if (variant.available === false) {
        track('variant_unavailable_selected', {
          product_id: context().product_id,
          variant_id: variant.id,
          option_changed: delta.option_changed,
          option_position: delta.option_position,
          option_value: delta.option_value,
          surface: surface,
        });
      }
    });

    document.addEventListener('openable-element:open', function (event) {
      var target = event.target;
      var tag = target.tagName;

      if (tag === 'QUICK-BUY-POPOVER' || tag === 'QUICK-BUY-DRAWER') {
        track('quick_buy_opened', {
          surface: surfaceFor(target),
          product_handle: handleFromHref(target.getAttribute('href')),
        });
        return;
      }

      if (String(target.id).indexOf('size-chart') !== -1) {
        track('size_chart_opened', {
          product_id: context().product_id,
          surface: surfaceFor(target),
        });
      }
    });

    document.addEventListener('openable-element:close', function (event) {
      var target = event.target;
      var tag = target.tagName;
      if (tag !== 'QUICK-BUY-POPOVER' && tag !== 'QUICK-BUY-DRAWER') return;

      track('quick_buy_closed', {
        surface: surfaceFor(target),
        product_handle: handleFromHref(target.getAttribute('href')),
      }, { destinations: ['pixel'] });
    });
  }

  var ZERO_RESULT_WATCH_MS = 3000;

  /**
   * The active filter values in a query string. sort_by, page, q and every
   * other param are not filters. Empty values are dropped: the facet form
   * submits both price inputs whether or not they hold a value.
   */
  function activeFilters(params) {
    var filters = [];

    params.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0 && value !== '') filters.push({ key: key, value: value });
    });

    return filters;
  }

  function filtersMissingFrom(filters, others) {
    return filters.filter(function (filter) {
      return !others.some(function (other) {
        return other.key === filter.key && other.value === filter.value;
      });
    });
  }

  /**
   * Classifies one facet:criteria-changed as exactly one of sort_changed,
   * filters_cleared, filter_applied or filter_removed. Returns null when no
   * filter or sort actually changed.
   *
   * `intent` carries what the URL diff cannot tell apart:
   *   - 'sort': <sort-by-popover> dispatched the change. Sorting is never read
   *     off the URL, because the facet form carries a hidden sort_by input:
   *     the first filter change on a page whose URL has no sort_by adds
   *     sort_by=<default> next to the filter, and diffing reported that as a
   *     sort.
   *   - 'clear': a clear-all link was clicked. With one active filter, clear-all
   *     and that filter's tag produce the same URL, and a price tag drops two
   *     params at once, so neither the URL nor a count of removed params can
   *     identify a clear.
   */
  function facetChange(nextUrl, intent) {
    var next;
    try {
      next = new URL(nextUrl, window.location.origin).searchParams;
    } catch (error) {
      return null;
    }

    var nextFilters = activeFilters(next);
    var currentFilters = activeFilters(new URLSearchParams(window.location.search));
    var added = filtersMissingFrom(nextFilters, currentFilters);
    var removed = filtersMissingFrom(currentFilters, nextFilters);
    var activeFilterCount = nextFilters.length;

    if (intent === 'sort') {
      return {
        name: 'sort_changed',
        props: { sort_by: next.get('sort_by'), active_filter_count: activeFilterCount },
        activeFilterCount: activeFilterCount,
      };
    }

    if (intent === 'clear' && removed.length) {
      return {
        name: 'filters_cleared',
        props: { cleared_filter_count: currentFilters.length },
        activeFilterCount: activeFilterCount,
      };
    }

    // A changed price bound is both an addition and a removal; it is reported
    // as the new value applied.
    var changed = added[0] || removed[0];
    if (!changed) return null;

    return {
      name: added.length ? 'filter_applied' : 'filter_removed',
      props: {
        filter_key: changed.key,
        filter_value: changed.value,
        active_filter_count: activeFilterCount,
      },
      activeFilterCount: activeFilterCount,
    };
  }

  /**
   * Calls `callback` once, the first time `element` or its subtree mutates.
   * The timeout path deliberately does NOT call back: if nothing mutated,
   * no re-render happened and there is nothing to report.
   */
  function observeSettle(element, callback, timeoutMs) {
    if (!element || typeof MutationObserver === 'undefined') return null;

    var settled = false;

    function finish() {
      if (settled) return;
      settled = true;
      observer.disconnect();
      callback();
    }

    var observer = new MutationObserver(finish);

    observer.observe(element, { childList: true, subtree: true });
    setTimeout(function () {
      if (settled) return;
      settled = true;
      observer.disconnect();
    }, timeoutMs);

    return observer;
  }

  var pendingZeroResultObserver = null;

  function watchForZeroResults(onEmpty) {
    if (pendingZeroResultObserver) {
      pendingZeroResultObserver.disconnect();
      pendingZeroResultObserver = null;
    }

    // Observe the container the theme REWRITES, not the grid it replaces.
    // ProductFacet._rerender sets `#facet-main`.innerHTML (assets/theme.js:5918),
    // which detaches `.product-list__inner` without producing a childList record
    // ON that node — so an observer bound to the grid never fires and
    // filter_zero_results would be silently dead in production.
    var container = document.querySelector('#facet-main')
      || document.querySelector('.product-list__inner');
    if (!container) return;

    var observer = observeSettle(container, function () {
      if (pendingZeroResultObserver === observer) pendingZeroResultObserver = null;
      // Re-query inside the container: the grid element itself may have been
      // replaced, so a reference captured earlier would be stale.
      if (container.querySelectorAll('product-item').length === 0) onEmpty();
    }, ZERO_RESULT_WATCH_MS);

    pendingZeroResultObserver = observer;
  }

  function registerDiscoveryEvents() {
    // FacetFilters dispatches facet:criteria-changed from itself
    // (assets/theme.js:5973), so the clicked link is not on that event. It is
    // recorded here, in the capture phase, before the theme's root-delegated
    // click handler turns the click into the facet change synchronously, and
    // forgotten once the click has finished propagating.
    var clickedClearAll = false;

    document.addEventListener('click', function (event) {
      clickedClearAll = !!(event.target && event.target.closest
        && event.target.closest('[data-analytics-event="filters_cleared"]'));
    }, true);

    window.addEventListener('click', function () {
      clickedClearAll = false;
    });

    document.addEventListener('facet:criteria-changed', function (event) {
      var target = event.target;
      var intent = null;

      if (target && target.closest && target.closest('sort-by-popover')) {
        intent = 'sort';
      } else if (clickedClearAll) {
        intent = 'clear';
      }

      var change = facetChange((event.detail || {}).url, intent);
      if (!change) return;

      change.props.collection_handle = context().collection_handle;
      track(change.name, change.props);

      watchForZeroResults(function () {
        track('filter_zero_results', {
          active_filter_count: change.activeFilterCount,
          collection_handle: context().collection_handle,
        });
      });
    }, true);

    document.addEventListener('click', function (event) {
      var button = event.target && event.target.closest
        ? event.target.closest('#load-more-button')
        : null;
      if (!button) return;

      var nextUrl = button.getAttribute('data-next-url');
      var page = null;

      try {
        page = parseInt(new URL(nextUrl, window.location.origin).searchParams.get('page'), 10);
      } catch (error) {
        page = null;
      }

      track('collection_load_more', {
        page_number: isFinite(page) ? page : null,
        collection_handle: context().collection_handle,
      });
    });

    // Capture phase, for the same reason as facet:criteria-changed above:
    // <page-pagination>'s own click handler dispatches pagination:page-changed
    // (assets/theme.js:1639), and ProductFacet._rerender rewrites
    // window.location to the new page as its first statement
    // (assets/theme.js:5907). A bubble-phase listener on document would run
    // after both and read the target page back as the page being left.
    //
    // AJAX pagination only (collection and search): the other templates do a
    // full navigation, which can unload the page before the event sends.
    document.addEventListener('click', function (event) {
      var link = event.target && event.target.closest
        ? event.target.closest('page-pagination[ajax] a[data-page]')
        : null;
      if (!link) return;

      var rel = link.getAttribute('rel');
      var current = pageNumberFrom(new URLSearchParams(window.location.search).get('page'));

      track('pagination_clicked', {
        page_number: pageNumberFrom(link.getAttribute('data-page')),
        previous_page_number: current || 1,
        total_pages: pageNumberFrom(link.closest('page-pagination').getAttribute('data-total-pages')),
        link_type: rel === 'next' ? 'next' : rel === 'prev' ? 'previous' : 'number',
        collection_handle: context().collection_handle,
      });
    }, true);
  }

  function pageNumberFrom(value) {
    var page = parseInt(value, 10);
    return isFinite(page) ? page : null;
  }

  var PREDICTIVE_DEBOUNCE_MS = 500;
  var PREDICTIVE_SETTLE_MS = 1500;
  var MIN_QUERY_LENGTH = 2;

  function registerSearchEvents() {
    var ctx = context();

    // The results page knows its exact count in Liquid (search.results_count,
    // sections/main-search.liquid:71), delivered through the context snippet.
    // That number is authoritative; the DOM is not consulted here.
    if (ctx.page_type === 'search' && ctx.search_terms) {
      var count = Number(ctx.search_results_count) || 0;

      track('search_performed', {
        query: ctx.search_terms,
        query_length: String(ctx.search_terms).length,
        results_count: count,
        search_type: 'full',
      });

      if (count === 0) {
        track('search_zero_results', {
          query: ctx.search_terms,
          search_type: 'full',
        });
      }
    }

    var debounceTimer = null;

    document.addEventListener('input', function (event) {
      var input = event.target;
      if (!input.classList || !input.classList.contains('predictive-search__input')) return;

      var query = String(input.value || '').trim();

      clearTimeout(debounceTimer);
      if (query.length < MIN_QUERY_LENGTH) return;

      debounceTimer = setTimeout(function () {
        var results = document.querySelector('.predictive-search__results');
        if (!results) return;

        function report() {
          // Predictive results are <li class="predictive-search__product-item">
          // (sections/predictive-search.liquid:67) — NOT the <product-item>
          // custom element the collection grid uses. Counting 'product-item'
          // here would always yield 0 and fire search_zero_results on every
          // predictive search, including ones that returned results.
          var predictiveCount = results.querySelectorAll('.predictive-search__product-item').length;

          track('search_performed', {
            query: query,
            query_length: query.length,
            results_count: predictiveCount,
            search_type: 'predictive',
          });

          if (predictiveCount === 0) {
            track('search_zero_results', { query: query, search_type: 'predictive' });
          }
        }

        // The theme's own debounce is 200ms (assets/theme.js:4681) and it
        // sets resultsElement.hidden = false then resultsElement.innerHTML
        // synchronously once the fetch resolves (assets/theme.js:4716,
        // :4722) — never with a yield to the event loop in between. So by
        // the time our 500ms debounce fires, `results` is either still
        // `hidden` (fetch not done — wait for the swap below) or already
        // unhidden AND populated (the round trip beat us — nothing will
        // mutate again, so observeSettle would time out and silently never
        // call back, biasing the data toward slow connections). Read
        // immediately in the latter case.
        if (results.hidden === false) {
          report();
          return;
        }

        // Predictive results arrive over AJAX, so the count must be read from
        // the DOM once they settle rather than from Liquid.
        observeSettle(results, report, PREDICTIVE_SETTLE_MS);
      }, PREDICTIVE_DEBOUNCE_MS);
    });
  }

  /**
   * Same-origin links report their pathname; an off-site link keeps its full
   * URL, since a bare path would read as a page on this store.
   */
  function navLinkUrl(href) {
    if (!href) return null;

    try {
      var url = new URL(href, window.location.origin);
      return url.origin === window.location.origin ? url.pathname : url.href;
    } catch (error) {
      return null;
    }
  }

  function registerNavigationEvents() {
    // snippets/desktop-menu.liquid and snippets/mobile-menu.liquid mark every
    // tracked element with data-analytics-nav ("link", "image" or "toggle"),
    // data-nav-level and data-menu-trail, and each menu root with
    // data-nav-menu. The A/B arm comes from the data-ab-menu wrapper in
    // sections/header.liquid.
    //
    // Bubble phase on window, so this runs after the theme's own handlers:
    //   - On touch devices DesktopNavigation (assets/theme.js:4958) cancels
    //     the first tap on a parent link to open its dropdown instead.
    //     defaultPrevented is only set by the time the click reaches here.
    //   - ToggleButton (assets/theme.js:1657) has already flipped
    //     aria-expanded, so "true" means this click opened the submenu.
    // The browser navigates only after dispatch finishes, so this still runs
    // before the page unloads. An event still queued for a destination that
    // has not loaded yet is lost with the page.
    window.addEventListener('click', function (event) {
      var element = event.target && event.target.closest
        ? event.target.closest('[data-analytics-nav]')
        : null;
      if (!element) return;

      var kind = element.getAttribute('data-analytics-nav');
      var menuTrail = element.getAttribute('data-menu-trail');
      var level = pageNumberFrom(element.getAttribute('data-nav-level'));

      if (kind === 'toggle') {
        if (element.getAttribute('aria-expanded') !== 'true') return;

        // Pixel-only on volume grounds, like the drawer close events.
        track('nav_section_expanded', { menu_trail: menuTrail, level: level }, { destinations: ['pixel'] });
        return;
      }

      if (event.defaultPrevented) return;

      var menu = element.closest('[data-nav-menu]');
      var abArm = element.closest('[data-ab-menu]');

      track('nav_link_clicked', {
        menu: menu ? menu.getAttribute('data-nav-menu') : null,
        menu_variant: abArm ? abArm.getAttribute('data-ab-menu') : null,
        level: level,
        link_type: kind === 'image' ? 'image' : 'text',
        menu_trail: menuTrail,
        link_url: navLinkUrl(element.getAttribute('href')),
      });
    });
  }

  window.BLAnalytics = {
    track: track,
    _internals: {
      isValidKey: isValidKey,
      sanitizeProps: sanitizeProps,
      buildEnvelope: buildEnvelope,
      surfaceFor: surfaceFor,
      toCents: toCents,
      flushQueue: flushAll,
      cartState: cartState,
      cartHasDiscount: cartHasDiscount,
      findCartItem: findCartItem,
      shippingEligibleCents: shippingEligibleCents,
      optionDelta: optionDelta,
      facetChange: facetChange,
      observeSettle: observeSettle,
      queues: {
        triplewhale: destinations.triplewhale.queue,
        pixel: destinations.pixel.queue,
      },
      debug: debug,
    },
  };

  ready(function () {
    registerLeadEvents();
    registerCartEvents();
    registerProductEvents();
    registerDiscoveryEvents();
    registerSearchEvents();
    registerNavigationEvents();
  });
})();
